import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import type {
  AreaPostcode,
  AreaSummaryResponse,
  Competitor,
  CompetitorRecruitStatus,
  PostcodeBand,
} from "@breeze/shared";
import { TopNav } from "./TopNav.js";
import { Section } from "./BusinessGrowth.js";
import {
  fetchAreaSummary,
  fetchCompetitorsByOutward,
  patchCompetitor,
  setPostcodeBand,
  shortlistFromCompetitor,
  syncPlaces,
} from "../api.js";
import { setView } from "../store/view.js";

// ===========================================================================
// Area Analysis dashboard.
//
// Layer-toggle map of UK outward postcodes:
//   - Revenue: 12-month £ heat — see where existing demand is
//   - Coverage: green = a tech covers it / red = uncovered
//   - Wealth: premium (teal) / mid (amber) / value (grey) bands
//
// Click an outward → side panel with full breakdown + manual band override.
// Right side: ranked top-revenue and uncovered-but-active lists.
//
// Phase 5+ adds competitor density and CPC overlays — schema accommodates.
// ===========================================================================

type Props = { userName: string };

// Tuned for the light Voyager tile theme. Saturated colours pop on
// cream; "value" / "unknown" use slate-500 which is readable but
// recedes vs. the active categories.
const BAND_COLORS: Record<PostcodeBand, string> = {
  premium: "#2EB67D",  // teal-green — pops on cream
  mid: "#ECB22E",      // amber
  value: "#64748b",    // slate-500
  unknown: "#64748b",  // same hue, distinguished by lower opacity
};

// Used for "no recent activity" / "no data" dot states across all layers.
// Tuned for the LIGHT (CARTO Voyager) tile theme — slate-500 is dark
// enough to read on the cream-white base map but visually subordinate
// to the saturated colours used for active states.
const NEUTRAL_FILL = "#64748b";   // slate-500
const NEUTRAL_STROKE = "#475569"; // slate-600

const BAND_LABELS: Record<PostcodeBand, string> = {
  premium: "Premium",
  mid: "Mid",
  value: "Value",
  unknown: "Untagged",
};

type LayerMode = "revenue" | "coverage" | "wealth" | "competition";

export function AreaAnalysis({ userName }: Props): JSX.Element {
  const [layer, setLayer] = useState<LayerMode>("revenue");
  const [selected, setSelected] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const q = useQuery<AreaSummaryResponse>({
    queryKey: ["areas", "summary", 365],
    queryFn: () => fetchAreaSummary(365),
  });

  const selectedPc = useMemo<AreaPostcode | null>(() => {
    if (!selected || !q.data) return null;
    return q.data.postcodes.find((p) => p.outward === selected) ?? null;
  }, [selected, q.data]);

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav userName={userName} />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-ink-100">
                Area Analysis
              </div>
              <h1 className="mt-1 text-2xl font-semibold text-ink-50">
                UK postcodes — wealth × revenue × coverage
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHelp(true)}
                className="rounded-md border border-ink-500 bg-ink-700 px-3 py-1.5 text-[13px] font-semibold text-ink-50 hover:bg-ink-600"
                title="How to use this dashboard"
              >
                How to use
              </button>
              <div className="flex items-center gap-1 rounded-md border border-ink-500 bg-ink-700 p-0.5">
                <LayerButton label="Revenue" active={layer === "revenue"} onClick={() => setLayer("revenue")} />
                <LayerButton label="Coverage" active={layer === "coverage"} onClick={() => setLayer("coverage")} />
                <LayerButton label="Wealth" active={layer === "wealth"} onClick={() => setLayer("wealth")} />
                <LayerButton label="Competition" active={layer === "competition"} onClick={() => setLayer("competition")} />
              </div>
            </div>
          </div>

          {showHelp && <HowToUseModal onClose={() => setShowHelp(false)} />}

          <Section
            title="Map"
            subtitle="Last 12 months · click any outward for details · right-click coming in Phase 5"
          >
            {q.isLoading ? (
              <div className="text-[15px] text-ink-100">Loading…</div>
            ) : !q.data ? (
              <div className="text-[15px] text-ink-100">No data.</div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <AreaMap
                    data={q.data}
                    layer={layer}
                    selectedOutward={selected}
                    onSelect={setSelected}
                  />
                  <div className="mt-2 flex items-center gap-3 text-[13px] text-ink-100">
                    <Legend layer={layer} />
                    <span className="ml-auto">
                      {q.data.postcodes.length} outwards · {q.data.postcodes.filter((p) => p.jobs12mo > 0).length} active in last 12mo
                    </span>
                  </div>
                </div>
                <PostcodeDetail pc={selectedPc} onClear={() => setSelected(null)} />
              </div>
            )}
          </Section>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <TopRevenueList data={q.data} onSelect={setSelected} />
            <UncoveredHotList data={q.data} onSelect={setSelected} />
          </div>
        </div>
      </main>
    </div>
  );
}

function LayerButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors ${
        active ? "bg-aubergine text-ink-50" : "text-ink-100 hover:bg-ink-600 hover:text-ink-50"
      }`}
    >
      {label}
    </button>
  );
}

// ---------- Map ---------------------------------------------------------------

function AreaMap({
  data,
  layer,
  selectedOutward,
  onSelect,
}: {
  data: AreaSummaryResponse;
  layer: LayerMode;
  selectedOutward: string | null;
  onSelect: (o: string | null) => void;
}): JSX.Element {
  const maxRevenue = useMemo(() => {
    let m = 0;
    for (const p of data.postcodes) if (p.revenue12mo > m) m = p.revenue12mo;
    return m;
  }, [data.postcodes]);
  const maxJobs = useMemo(() => {
    let m = 0;
    for (const p of data.postcodes) if (p.jobs12mo > m) m = p.jobs12mo;
    return m;
  }, [data.postcodes]);
  const maxCompetitors = useMemo(() => {
    let m = 0;
    for (const p of data.postcodes) if (p.competitorCount > m) m = p.competitorCount;
    return m;
  }, [data.postcodes]);
  // UK-wide default centre for the strategic view.
  const center: [number, number] = [54.0, -2.0];
  const zoom = 6;
  return (
    <div className="h-[520px] w-full overflow-hidden rounded-lg border border-ink-700">
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={true}
        // Light cream background — switched from CARTO Dark Matter (dark)
        // because Darren can't read the country/region labels on dark.
        style={{ height: "100%", width: "100%", background: "#f5f3ee" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {data.postcodes.map((p) => (
          <Marker
            key={p.outward}
            p={p}
            layer={layer}
            maxRevenue={maxRevenue}
            maxJobs={maxJobs}
            maxCompetitors={maxCompetitors}
            isSelected={selectedOutward === p.outward}
            onSelect={onSelect}
          />
        ))}
      </MapContainer>
    </div>
  );
}

function Marker({
  p,
  layer,
  maxRevenue,
  maxJobs,
  maxCompetitors,
  isSelected,
  onSelect,
}: {
  p: AreaPostcode;
  layer: LayerMode;
  maxRevenue: number;
  maxJobs: number;
  maxCompetitors: number;
  isSelected: boolean;
  onSelect: (o: string | null) => void;
}): JSX.Element {
  const radius = computeRadius(p, layer, maxJobs, maxCompetitors);
  const { fill, stroke, fillOpacity } = computeColor(p, layer, maxRevenue, maxCompetitors, isSelected);
  return (
    <CircleMarker
      center={[p.latitude, p.longitude]}
      radius={radius}
      pathOptions={{
        color: stroke,
        fillColor: fill,
        fillOpacity,
        weight: isSelected ? 3 : 1,
      }}
      eventHandlers={{
        click: () => onSelect(isSelected ? null : p.outward),
      }}
    >
      <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
        <div style={{ fontSize: 12, lineHeight: 1.4 }}>
          <div style={{ fontWeight: 600 }}>{p.outward}</div>
          <div>£{p.revenue12mo.toLocaleString("en-GB", { maximumFractionDigits: 0 })} · {p.jobs12mo} jobs</div>
          <div style={{ color: "#aaa" }}>
            {BAND_LABELS[p.band]} · {p.covered ? `${p.techCount} tech${p.techCount === 1 ? "" : "s"}` : "uncovered"}
            {p.competitorCount > 0 && ` · ${p.competitorCount} comp${p.competitorCount === 1 ? "" : "s"}`}
          </div>
        </div>
      </Tooltip>
    </CircleMarker>
  );
}

function computeRadius(
  p: AreaPostcode,
  layer: LayerMode,
  maxJobs: number,
  maxCompetitors: number
): number {
  if (layer === "competition") {
    if (maxCompetitors === 0) return 4;
    const t = Math.sqrt(p.competitorCount / maxCompetitors);
    return 4 + t * 14;
  }
  if (layer === "wealth" || layer === "coverage") return p.jobs12mo > 0 ? 6 : 4;
  if (maxJobs === 0) return 4;
  const t = Math.sqrt(p.jobs12mo / maxJobs);
  return 4 + t * 14;
}

function computeColor(
  p: AreaPostcode,
  layer: LayerMode,
  maxRevenue: number,
  maxCompetitors: number,
  isSelected: boolean
): { fill: string; stroke: string; fillOpacity: number } {
  // Dark stroke on light map — white selection ring disappears on cream.
  const selectedStroke = "#0f172a"; // slate-900
  if (layer === "wealth") {
    const c = BAND_COLORS[p.band];
    return {
      fill: c,
      stroke: isSelected ? selectedStroke : c,
      // Untagged stays subordinate but readable; banded postcodes pop.
      fillOpacity: p.band === "unknown" ? 0.45 : 0.7,
    };
  }
  if (layer === "coverage") {
    if (p.covered) {
      return { fill: "#2EB67D", stroke: isSelected ? selectedStroke : "#2EB67D", fillOpacity: 0.7 };
    }
    if (p.jobs12mo > 0) {
      // Uncovered but we DO get jobs there — opportunity / risk
      return { fill: "#E01E5A", stroke: isSelected ? selectedStroke : "#E01E5A", fillOpacity: 0.75 };
    }
    return { fill: NEUTRAL_FILL, stroke: isSelected ? selectedStroke : NEUTRAL_STROKE, fillOpacity: 0.4 };
  }
  if (layer === "competition") {
    // Inverted heatmap — red = many competitors, green = few/none. The
    // strategic read is "where can we win" (low competition + wealth +
    // demand), so few competitors should look attractive.
    if (p.competitorCount === 0) {
      return { fill: "#2EB67D", stroke: isSelected ? selectedStroke : "#2EB67D", fillOpacity: 0.55 };
    }
    if (maxCompetitors === 0) {
      return { fill: "#36C5F0", stroke: isSelected ? selectedStroke : "#36C5F0", fillOpacity: 0.7 };
    }
    const t = Math.min(1, p.competitorCount / maxCompetitors);
    const fill = t < 0.33 ? "#36C5F0" : t < 0.66 ? "#ECB22E" : t < 0.85 ? "#F07B3F" : "#E01E5A";
    return { fill, stroke: isSelected ? selectedStroke : fill, fillOpacity: 0.7 };
  }
  // revenue
  if (p.jobs12mo === 0) {
    return { fill: NEUTRAL_FILL, stroke: isSelected ? selectedStroke : NEUTRAL_STROKE, fillOpacity: 0.4 };
  }
  if (maxRevenue === 0) {
    return { fill: "#36C5F0", stroke: isSelected ? selectedStroke : "#36C5F0", fillOpacity: 0.55 };
  }
  const t = Math.min(1, p.revenue12mo / maxRevenue);
  const fill = t < 0.33 ? "#36C5F0" : t < 0.66 ? "#2EB67D" : t < 0.85 ? "#ECB22E" : "#E01E5A";
  return { fill, stroke: isSelected ? selectedStroke : fill, fillOpacity: 0.6 };
}

function Legend({ layer }: { layer: LayerMode }): JSX.Element {
  if (layer === "wealth") {
    return (
      <div className="flex items-center gap-3">
        {(Object.keys(BAND_COLORS) as PostcodeBand[]).map((b) => (
          <span key={b} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: BAND_COLORS[b] }} />
            <span>{BAND_LABELS[b]}</span>
          </span>
        ))}
      </div>
    );
  }
  if (layer === "coverage") {
    return (
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#2EB67D" }} />covered</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#E01E5A" }} />uncovered (with demand)</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: NEUTRAL_FILL }} />no recent jobs</span>
      </div>
    );
  }
  if (layer === "competition") {
    return (
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#2EB67D" }} />none yet</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#36C5F0" }} />few</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#ECB22E" }} />some</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#F07B3F" }} />many</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#E01E5A" }} />saturated</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#36C5F0" }} />low</span>
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#2EB67D" }} />steady</span>
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#ECB22E" }} />busy</span>
      <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-full" style={{ background: "#E01E5A" }} />top</span>
    </div>
  );
}

// ---------- Side panel (with band override) ----------------------------------

function PostcodeDetail({
  pc,
  onClear,
}: {
  pc: AreaPostcode | null;
  onClear: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  // Pull competitors when an outward is selected.
  const compsQ = useQuery<Competitor[]>({
    queryKey: ["areas", "competitors", pc?.outward ?? ""],
    queryFn: () => fetchCompetitorsByOutward(pc!.outward),
    enabled: Boolean(pc),
  });

  if (!pc) {
    return (
      <div className="rounded-lg border border-ink-700 bg-ink-700/40 p-4 text-[13px] text-ink-100">
        Click an outward on the map for details and to mark it as premium / mid / value.
      </div>
    );
  }

  async function handleSetBand(band: PostcodeBand): Promise<void> {
    if (!pc) return;
    setSaving(true);
    setError(null);
    try {
      await setPostcodeBand(pc.outward, { band });
      await qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSyncPlaces(): Promise<void> {
    if (!pc) return;
    setSyncing(true);
    setSyncMsg(null);
    setError(null);
    try {
      const r = await syncPlaces(pc.outward);
      setSyncMsg(`Pulled ${r.inserted + r.updated} from Google Places (${r.inserted} new, ${r.updated} refreshed)`);
      await qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="rounded-lg border border-ink-700 bg-ink-700/40 p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xl font-semibold font-mono text-ink-50">{pc.outward}</h3>
        <button onClick={onClear} className="text-[13px] text-ink-100 hover:text-ink-50">✕ clear</button>
      </div>
      {pc.region && <div className="mt-1 text-[13px] text-ink-100">{pc.region}</div>}

      <div className="mt-4 space-y-2 text-[15px] text-ink-50">
        <Row label="Revenue (12mo)" value={`£${pc.revenue12mo.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`} />
        <Row label="Jobs (12mo)" value={String(pc.jobs12mo)} />
        <Row label="Last job" value={pc.lastJobAt ?? "—"} />
        <Row
          label="Coverage"
          value={pc.covered ? `${pc.techCount} tech${pc.techCount === 1 ? "" : "s"}` : "uncovered"}
          valueClass={pc.covered ? "text-stage-won" : "text-stage-lost"}
        />
        <Row
          label="IMD decile"
          value={pc.imdDecile === null ? "—" : `${pc.imdDecile}/10`}
          hint={pc.imdDecile === null ? "not seeded" : undefined}
        />
      </div>

      <div className="mt-4">
        <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">Wealth band</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(["premium", "mid", "value", "unknown"] as PostcodeBand[]).map((b) => (
            <button
              key={b}
              onClick={() => void handleSetBand(b)}
              disabled={saving}
              className={`rounded-md px-3 py-1 text-[13px] font-semibold transition-colors disabled:opacity-50 ${
                pc.band === b
                  ? "text-ink-50"
                  : "border border-ink-500 text-ink-100 hover:bg-ink-600 hover:text-ink-50"
              }`}
              style={pc.band === b ? { background: BAND_COLORS[b] } : undefined}
            >
              {BAND_LABELS[b]}
            </button>
          ))}
        </div>
        <div className="mt-2 text-[13px] text-ink-100">
          {pc.bandSource === "manual_override"
            ? `Set by ${pc.bandUpdatedBy ?? "someone"}`
            : pc.bandSource === "imd_seed"
            ? "Seeded from IMD"
            : "Not yet tagged"}
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-stage-lost/40 bg-stage-lost/10 p-2 text-[13px] text-stage-lost">
          {error}
        </div>
      )}

      <div className="mt-5 border-t border-ink-700 pt-4">
        <div className="flex items-center justify-between">
          <div className="text-[12px] uppercase tracking-[0.14em] text-ink-100">
            Competitors ({pc.competitorCount})
          </div>
          <button
            onClick={() => void handleSyncPlaces()}
            disabled={syncing}
            className="rounded-md bg-aubergine px-2 py-1 text-[11px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
            title="Pull this postcode's competitors from Google Places (~$0.03)"
          >
            {syncing ? "Syncing…" : "Sync Google"}
          </button>
        </div>
        {syncMsg && (
          <div className="mt-2 rounded-md border border-stage-won/40 bg-stage-won/10 p-2 text-[12px] text-stage-won">
            {syncMsg}
          </div>
        )}
        {compsQ.isLoading ? (
          <div className="mt-2 text-[13px] text-ink-100">Loading…</div>
        ) : !compsQ.data || compsQ.data.length === 0 ? (
          <div className="mt-2 text-[13px] text-ink-100">
            No competitors scraped yet. Click "Sync Google" or wait for the daily scraper sweeps.
          </div>
        ) : (
          <ul className="mt-2 space-y-2">
            {compsQ.data.slice(0, 10).map((c) => (
              <CompetitorCard key={c.id} c={c} outward={pc.outward} />
            ))}
            {compsQ.data.length > 10 && (
              <li className="text-[12px] text-ink-100">+{compsQ.data.length - 10} more</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  hint,
  valueClass,
}: {
  label: string;
  value: string;
  hint?: string;
  valueClass?: string;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-ink-100">{label}</span>
      <span className={`text-right font-semibold tabular-nums ${valueClass ?? "text-ink-50"}`}>
        {value}
        {hint && <span className="ml-2 font-normal text-[12px] text-ink-100">({hint})</span>}
      </span>
    </div>
  );
}

// ---------- Lists ------------------------------------------------------------

function TopRevenueList({
  data,
  onSelect,
}: {
  data: AreaSummaryResponse | undefined;
  onSelect: (o: string | null) => void;
}): JSX.Element {
  const rows = useMemo(() => {
    if (!data) return [];
    return [...data.postcodes]
      .filter((p) => p.revenue12mo > 0)
      .sort((a, b) => b.revenue12mo - a.revenue12mo)
      .slice(0, 20);
  }, [data]);
  return (
    <Section title="Top postcodes by revenue" subtitle="Last 12 months">
      {rows.length === 0 ? (
        <div className="text-[15px] text-ink-100">No revenue data.</div>
      ) : (
        <ul className="divide-y divide-ink-700">
          {rows.map((p) => (
            <li
              key={p.outward}
              onClick={() => onSelect(p.outward)}
              className="flex cursor-pointer items-baseline justify-between py-2 hover:bg-ink-700/40"
            >
              <span className="flex items-baseline gap-3">
                <span className="font-mono font-semibold text-ink-50">{p.outward}</span>
                <span className="rounded-md px-1.5 py-0.5 text-[11px] font-semibold" style={{ background: `${BAND_COLORS[p.band]}33`, color: BAND_COLORS[p.band] }}>
                  {BAND_LABELS[p.band]}
                </span>
                {!p.covered && (
                  <span className="rounded-md bg-stage-lost/20 px-1.5 py-0.5 text-[11px] font-semibold text-stage-lost">
                    uncovered
                  </span>
                )}
              </span>
              <span className="font-semibold tabular-nums text-ink-50">
                £{p.revenue12mo.toLocaleString("en-GB", { maximumFractionDigits: 0 })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function UncoveredHotList({
  data,
  onSelect,
}: {
  data: AreaSummaryResponse | undefined;
  onSelect: (o: string | null) => void;
}): JSX.Element {
  // Active demand we DON'T cover — biggest recruitment / outbound signal.
  const rows = useMemo(() => {
    if (!data) return [];
    return [...data.postcodes]
      .filter((p) => p.jobs12mo > 0 && !p.covered)
      .sort((a, b) => b.revenue12mo - a.revenue12mo)
      .slice(0, 20);
  }, [data]);
  return (
    <Section
      title="Uncovered demand"
      subtitle="Postcodes with jobs but no tech claiming coverage — recruit or extend coverage here"
    >
      {rows.length === 0 ? (
        <div className="text-[15px] text-ink-100">Every active outward is covered. Nice.</div>
      ) : (
        <ul className="divide-y divide-ink-700">
          {rows.map((p) => (
            <li
              key={p.outward}
              onClick={() => onSelect(p.outward)}
              className="flex cursor-pointer items-baseline justify-between py-2 hover:bg-ink-700/40"
            >
              <span className="flex items-baseline gap-3">
                <span className="font-mono font-semibold text-ink-50">{p.outward}</span>
                <span className="rounded-md px-1.5 py-0.5 text-[11px] font-semibold" style={{ background: `${BAND_COLORS[p.band]}33`, color: BAND_COLORS[p.band] }}>
                  {BAND_LABELS[p.band]}
                </span>
                <span className="text-[13px] text-ink-100">{p.jobs12mo} jobs</span>
              </span>
              <span className="font-semibold tabular-nums text-ink-50">
                £{p.revenue12mo.toLocaleString("en-GB", { maximumFractionDigits: 0 })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------- Competitor card with recruitment controls (Phase 7) -------------

const RECRUIT_STATUSES: CompetitorRecruitStatus[] = [
  "not_contacted",
  "contacted",
  "interested",
  "rejected",
  "hired",
];

const RECRUIT_LABELS: Record<CompetitorRecruitStatus, string> = {
  not_contacted: "Not contacted",
  contacted: "Contacted",
  interested: "Interested",
  rejected: "Rejected",
  hired: "Hired",
};

const RECRUIT_TONES: Record<CompetitorRecruitStatus, string> = {
  not_contacted: "bg-ink-700 text-ink-100",
  contacted: "bg-stage-lead/20 text-stage-lead",
  interested: "bg-stage-opportunity/20 text-stage-opportunity",
  rejected: "bg-stage-lost/20 text-stage-lost",
  hired: "bg-stage-won/20 text-stage-won",
};

function CompetitorCard({ c, outward }: { c: Competitor; outward: string }): JSX.Element {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(c.recruitNotes ?? "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [shortlisting, setShortlisting] = useState(false);
  const isShortlisted = c.addedBy !== null;

  async function handleStatusChange(status: CompetitorRecruitStatus): Promise<void> {
    await patchCompetitor(c.id, { recruitStatus: status });
    await qc.invalidateQueries({ queryKey: ["areas", "competitors", outward] });
    await qc.invalidateQueries({ queryKey: ["areas", "recruitment"] });
    await qc.invalidateQueries({ queryKey: ["recruitment", "candidates"] });
  }

  async function handleShortlist(): Promise<void> {
    setShortlisting(true);
    try {
      await shortlistFromCompetitor(c.id);
      await qc.invalidateQueries({ queryKey: ["areas", "competitors", outward] });
      await qc.invalidateQueries({ queryKey: ["recruitment", "candidates"] });
    } finally {
      setShortlisting(false);
    }
  }

  async function handleSaveNotes(): Promise<void> {
    setSavingNotes(true);
    try {
      await patchCompetitor(c.id, { recruitNotes: notes.trim() || null });
      await qc.invalidateQueries({ queryKey: ["areas", "competitors", outward] });
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <li className="rounded-md border border-ink-700 bg-ink-800/40 p-2 text-[13px]">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold text-ink-50">{c.businessName}</span>
        <span className="text-[11px] text-ink-100">{c.source}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-3 text-[12px] text-ink-100">
        {c.rating !== null && (
          <span>★ {c.rating.toFixed(1)}{c.reviewCount ? ` (${c.reviewCount})` : ""}</span>
        )}
        {c.phone && <span className="font-mono">{c.phone}</span>}
        {c.website && (
          <a
            href={c.website}
            target="_blank"
            rel="noreferrer"
            className="text-ink-50 underline hover:text-stage-lead"
          >
            site
          </a>
        )}
      </div>
      {c.addressText && (
        <div className="mt-1 text-[11px] text-ink-100/80">{c.addressText}</div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          value={c.recruitStatus}
          onChange={(e) => void handleStatusChange(e.target.value as CompetitorRecruitStatus)}
          className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${RECRUIT_TONES[c.recruitStatus]}`}
        >
          {RECRUIT_STATUSES.map((s) => (
            <option key={s} value={s}>{RECRUIT_LABELS[s]}</option>
          ))}
        </select>
        {isShortlisted ? (
          <button
            onClick={() => setView("recruitment")}
            className="rounded-sm border border-aubergineLight px-1.5 py-0.5 text-[11px] text-aubergineLight hover:bg-aubergine/20"
            title="Open the recruitment board"
          >
            On recruitment board ↗
          </button>
        ) : (
          <button
            onClick={() => void handleShortlist()}
            disabled={shortlisting}
            className="rounded-sm bg-aubergine px-2 py-0.5 text-[11px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
          >
            {shortlisting ? "Adding…" : "+ Add to recruitment"}
          </button>
        )}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] text-ink-100 hover:text-ink-50"
        >
          {expanded ? "− notes" : "+ notes"}
        </button>
        {c.recruitNotes && !expanded && (
          <span className="truncate text-[11px] text-ink-100/80">{c.recruitNotes}</span>
        )}
      </div>

      {expanded && (
        <div className="mt-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Outreach notes — when contacted, response, follow-ups…"
            className="w-full rounded-md border border-ink-500 bg-ink-700 px-2 py-1 text-[12px] text-ink-50"
          />
          <div className="mt-1 flex justify-end">
            <button
              onClick={() => void handleSaveNotes()}
              disabled={savingNotes || notes === (c.recruitNotes ?? "")}
              className="rounded-md bg-aubergine px-2 py-1 text-[11px] font-semibold text-ink-50 hover:bg-aubergineLight disabled:opacity-40"
            >
              {savingNotes ? "Saving…" : "Save notes"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------- How-to-use modal -------------------------------------------------

function HowToUseModal({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div
      // z-[1000] beats Leaflet's tile/control panes (which sit at 200-700).
      // Without this the modal renders behind the map.
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-ink-50">How to use Area Analysis</h2>
          <button onClick={onClose} className="text-[20px] text-ink-100 hover:text-ink-50">×</button>
        </div>

        <div className="space-y-5 text-[14px] text-ink-50">
          <p>
            UK postcode map that overlays <span className="font-semibold">your business data</span> with public deprivation data and (optionally) competitor data. Most of what you see is your own — Google is only touched for the Competition layer.
          </p>

          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-100">The 4 layers</div>
            <ul className="mt-2 space-y-2">
              <li>
                <span className="font-semibold text-ink-50">Revenue</span> — circle size = job count, colour intensity = £ revenue. Last 12 months. Sourced from <span className="font-mono text-[12px]">B_TVISIT</span>.
              </li>
              <li>
                <span className="font-semibold text-ink-50">Coverage</span> — green = a tech covers it / red = jobs but no tech / neutral = no recent jobs. Sourced from <span className="font-mono text-[12px]">B_TUSERS.LOCATIONS_COVERED</span>.
              </li>
              <li>
                <span className="font-semibold text-ink-50">Wealth</span> — premium / mid / value bands seeded from the 2019 IMD (Indices of Multiple Deprivation, free ONS gov data). Click any postcode to override manually if your local knowledge says otherwise.
              </li>
              <li>
                <span className="font-semibold text-ink-50">Competition</span> — green = nobody else / amber-red = saturated. Pulled from Google Places API on demand (£0.025 per "Sync Google" click) or via the daily auto-sync (50 outwards/day).
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-100">The right-hand side panel</div>
            <p className="mt-2">
              Click any postcode on the map. The panel shows revenue, jobs, coverage, IMD decile, wealth band (with manual-override buttons), and the competitor list. Hit <span className="font-semibold">Sync Google</span> on a postcode to pull live competitors. Each competitor has a <span className="font-semibold">recruitment status dropdown</span> — flip "Not contacted → Contacted" as you message them, add notes, track Interested / Hired through to onboarding.
            </p>
          </div>

          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-100">The two lists at the bottom</div>
            <ul className="mt-2 space-y-2">
              <li>
                <span className="font-semibold text-ink-50">Top postcodes by revenue</span> — your real customer footprint last 12 months. Click any row to focus the map.
              </li>
              <li>
                <span className="font-semibold text-ink-50">Uncovered demand</span> — postcodes where you've had jobs but no tech currently claims coverage. <span className="text-stage-opportunity">This is your recruitment hit-list.</span> Sync Google on the top entries → outreach the top-rated competitors → flag as Contacted.
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-aubergine/40 bg-aubergine/10 p-3">
            <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-50">A typical 5-min recruitment pass</div>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-[14px]">
              <li>Switch to <span className="font-semibold">Coverage</span> layer — red dots are uncovered demand.</li>
              <li>Bottom-right: <span className="font-semibold">Uncovered demand</span> list. Pick the top 3-5 by £.</li>
              <li>Click each → <span className="font-semibold">Sync Google</span> in the side panel (~£0.075 total).</li>
              <li>Top 3-5 by rating ≥ 4.5 + reviews ≥ 20: change status to <span className="font-semibold">Contacted</span>, add notes after you message them.</li>
              <li>Switch to <span className="font-semibold">Wealth</span> layer to triangulate — high revenue + premium + uncovered = highest priority recruitment.</li>
            </ol>
          </div>

          <div className="text-[12px] text-ink-100">
            Most data refreshes automatically (job stats every page load, IMD seeded once, competitors via daily cron at 50/day). The Sync Google button is the only paid action — use it when you actively want fresh competitor data on a specific postcode.
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md bg-aubergine px-4 py-2 text-[14px] font-semibold text-ink-50 hover:bg-aubergineLight"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
